import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import copyText from '../../core/util/copyText';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import useAlterBookingState from '../hooks/useAlterBookingState';

interface Props {
    bookingId: number;
    onClose(): void;
    onSuccess?: () => void;
}

const checkinFormMessageQuery = gql`
    query CheckinFormMessageQuery($id: Int!) {
        guestDetailTemplate: messageTemplate(
            name: "Ask for guest detail form"
        ) {
            value
        }
        booking(id: $id) {
            id
            reference
            guestCheckinUrl
            guests {
                firstName
            }
        }
    }
`;

const BookingMessageGeneratorDialog = ({
    bookingId,
    onFocusLost,
    onSuccess,
}: {
    bookingId: number;
    onFocusLost: () => void;
    onSuccess?: () => void;
}) => {
    const [copied, setCopied] = useState(false);
    const {
        loading,
        data: {
            booking: { reference, guestCheckinUrl, guests },
            guestDetailTemplate: { value: guestDetailMessageTemplate },
        } = {
            booking: {
                reference: '',
                guestCheckinUrl: '',
                guestInstructionsUrl: '',
                guests: [{ firstName: '' }],
            },
            guestDetailTemplate: { value: '' },
            guestInstructionsTemplate: { value: '' },
        },
    } = useQuery<{
        booking: {
            reference: string;
            guestCheckinUrl: string;
            guestInstructionsUrl: string;
            guests: { firstName: string }[];
        };
        guestDetailTemplate: { value: string };
    }>(checkinFormMessageQuery, { variables: { id: bookingId } });

    const [alterBookingState, { loading: alterBookingStateLoading }] =
        useAlterBookingState(bookingId);

    if (loading || alterBookingStateLoading) {
        return <SpinnerIcon />;
    }

    const message = guestDetailMessageTemplate
        .replace('${firstName}', guests[0].firstName)
        .replace('${guestCheckinUrl}', guestCheckinUrl);

    return (
        <Dialog
            open={true}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                Guest Details Request
            </DialogTitle>
            <DialogContent
                sx={{
                    backgroundColor: '#1a1a2e',
                    p: 0,
                }}
            >
                <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                    <TextField
                        multiline
                        rows={6}
                        fullWidth
                        value={message}
                        label="Message"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& textarea': {
                                    backgroundColor: '#0f0f23',
                                    fontFamily: 'monospace',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '1rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="contained"
                    color={copied ? 'success' : 'secondary'}
                    onClick={() => {
                        copyText(message)
                            .then(() => {
                                setCopied(true);
                            })
                            .catch(error => {
                                throw error;
                            });
                    }}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid',
                        borderColor: copied ? 'success.main' : 'secondary.main',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                    }}
                >
                    {copied ? 'Copied ✓' : 'Copy message'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => {
                        window.open(
                            `https://www.airbnb.co.uk/hosting/reservations/details/${reference}`
                        );
                    }}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        },
                    }}
                >
                    Open booking
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        alterBookingState({
                            variables: {
                                id: bookingId,
                                fields: { detailsRequested: true },
                            },
                        })
                            .then(() => {
                                onSuccess?.();
                            })
                            .catch(error => {
                                throw error;
                            })
                            .finally(() => {
                                onFocusLost();
                            });
                    }}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                    }}
                >
                    Mark as sent
                </Button>
                <Button
                    variant="outlined"
                    onClick={onFocusLost}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#374151',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#374151',
                            backgroundColor: 'rgba(55, 65, 81, 0.1)',
                        },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const BookingMessageGenerator = ({ bookingId, onClose, onSuccess }: Props) => {
    return (
        <BookingMessageGeneratorDialog
            bookingId={bookingId}
            onFocusLost={onClose}
            onSuccess={onSuccess}
        />
    );
};

export default BookingMessageGenerator;
